import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChartPieIcon,
} from '@phosphor-icons/react';
import { useEffect, useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useEnhancedApi } from '@/hooks/use-enhanced-api';
import { useToast } from '@/hooks/use-toast';
import { formatCurrency, formatPercentage } from '@/utils';

interface ABCItem {
  id: string;
  name: string;
  sku: string;
  category: 'A' | 'B' | 'C';
  revenue: number;
  percentage: number;
  cumulativePercentage: number;
  quantity: number;
  unitPrice: number;
  trend: 'up' | 'down' | 'stable';
  trendValue: number;
}

interface ABCAnalysisData {
  items: ABCItem[];
  summary: {
    totalRevenue: number;
    categoryA: {
      count: number;
      revenue: number;
      percentage: number;
    };
    categoryB: {
      count: number;
      revenue: number;
      percentage: number;
    };
    categoryC: {
      count: number;
      revenue: number;
      percentage: number;
    };
  };
  recommendations: string[];
  lastUpdated: string;
}

interface ABCAnalysisProps {
  period?: string;
  className?: string;
}

const ABCAnalysis: React.FC<ABCAnalysisProps> = ({
  period = '30d',
  className = '',
}) => {
  const [data, setData] = useState<ABCAnalysisData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<'A' | 'B' | 'C' | 'all'>(
    'all'
  );

  const api = useEnhancedApi();
  const { toast } = useToast();
  const showToast = (message: string, success: boolean = true) => {
    toast({
      title: success ? 'Success' : 'Error',
      description: message,
      variant: success ? 'default' : 'destructive',
    });
  };

  useEffect(() => {
    fetchABCAnalysis();
  }, [period]);

  const fetchABCAnalysis = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.inventory.getABCAnalysis({ period });
      setData(response.data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch ABC analysis';
      setError(errorMessage);
      showToast(errorMessage, false);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category: 'A' | 'B' | 'C') => {
    switch (category) {
      case 'A':
        return 'text-success-60 bg-success-5 border-success-20';
      case 'B':
        return 'text-warning-60 bg-warning-5 border-warning-20';
      case 'C':
        return 'text-destructive-60 bg-destructive-5 border-destructive-20';
    }
  };

  const getCategoryDescription = (category: 'A' | 'B' | 'C') => {
    switch (category) {
      case 'A':
        return 'High-value items requiring tight control';
      case 'B':
        return 'Moderate-value items with standard control';
      case 'C':
        return 'Low-value items with simple controls';
    }
  };

  const getFilteredItems = () => {
    if (!data || activeCategory === 'all') return data?.items || [];
    return data.items.filter(item => item.category === activeCategory);
  };

  const getTrendIcon = (trend: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up':
        return <ArrowUpIcon className='h-4 w-4 text-success-60' />;
      case 'down':
        return <ArrowDownIcon className='h-4 w-4 text-destructive-60' />;
      case 'stable':
        return <div className='h-4 w-4 bg-gray-40 rounded-full' />;
    }
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <div className='flex items-center gap-2'>
            <ChartPieIcon className='h-5 w-5' />
            <Skeleton className='h-6 w-32' />
          </div>
          <Skeleton className='h-4 w-64' />
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='grid grid-cols-3 gap-4'>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className='h-20' />
            ))}
          </div>
          <div className='space-y-2'>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className='h-16 w-full' />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className='pt-6'>
          <Alert variant='destructive'>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  return (
    <Card className={className}>
      <CardHeader>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <ChartPieIcon className='h-5 w-5 text-brand-60' />
            <div>
              <CardTitle>ABC Analysis</CardTitle>
              <CardDescription>
                Revenue-based inventory categorization for optimal control
              </CardDescription>
            </div>
          </div>
          <Badge variant='outline' className='text-xs'>
            {data.lastUpdated}
          </Badge>
        </div>
      </CardHeader>

      <CardContent>
        {/* Summary Cards */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-6'>
          {Object.entries(data.summary)
            .filter(([key]) => key !== 'totalRevenue')
            .map(([key, categoryData]) => {
              const category = key.replace('category', '') as 'A' | 'B' | 'C';
              // Type guard to ensure categoryData has the expected structure
              if (
                typeof categoryData === 'number' ||
                !categoryData ||
                typeof categoryData !== 'object'
              ) {
                return null;
              }
              const data = categoryData as {
                count: number;
                revenue: number;
                percentage: number;
              };
              return (
                <Card key={key} className='relative overflow-hidden'>
                  <CardContent className='p-4'>
                    <div className='flex items-center justify-between mb-2'>
                      <Badge className={getCategoryColor(category)}>
                        Category {category}
                      </Badge>
                      <span className='text-sm text-gray-60'>
                        {data.count} items
                      </span>
                    </div>
                    <div className='space-y-1'>
                      <div className='text-2xl font-bold'>
                        {formatCurrency(data.revenue)}
                      </div>
                      <div className='text-sm text-gray-60'>
                        {formatPercentage(data.percentage)} of total
                      </div>
                      <Progress value={data.percentage} className='h-2' />
                    </div>
                    <p className='text-xs text-gray-50 mt-2'>
                      {getCategoryDescription(category)}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
        </div>

        {/* Category Tabs */}
        <Tabs
          value={activeCategory}
          onValueChange={value =>
            setActiveCategory(value as 'A' | 'B' | 'C' | 'all')
          }
        >
          <TabsList className='grid w-full grid-cols-4'>
            <TabsTrigger value='all'>All Items</TabsTrigger>
            <TabsTrigger value='A'>Category A</TabsTrigger>
            <TabsTrigger value='B'>Category B</TabsTrigger>
            <TabsTrigger value='C'>Category C</TabsTrigger>
          </TabsList>

          {/* Items List */}
          <TabsContent value={activeCategory} className='mt-4'>
            <div className='space-y-2 max-h-96 overflow-y-auto'>
              {getFilteredItems().map(item => (
                <Card key={item.id} className='p-4'>
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center gap-4'>
                      <Badge className={getCategoryColor(item.category)}>
                        {item.category}
                      </Badge>
                      <div>
                        <div className='font-medium'>{item.name}</div>
                        <div className='text-sm text-gray-60'>{item.sku}</div>
                      </div>
                    </div>

                    <div className='flex items-center gap-6 text-right'>
                      <div>
                        <div className='font-medium'>
                          {formatCurrency(item.revenue)}
                        </div>
                        <div className='text-sm text-gray-60'>
                          {formatPercentage(item.percentage)}
                        </div>
                      </div>

                      <div>
                        <div className='font-medium'>{item.quantity}</div>
                        <div className='text-sm text-gray-60'>units</div>
                      </div>

                      <div className='flex items-center gap-1'>
                        {getTrendIcon(item.trend)}
                        <span className='text-sm'>
                          {item.trend !== 'stable' &&
                            formatPercentage(Math.abs(item.trendValue))}
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}

              {getFilteredItems().length === 0 && (
                <div className='text-center py-8 text-gray-60'>
                  No items found for the selected category
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Recommendations */}
        {data.recommendations.length > 0 && (
          <Card className='mt-6'>
            <CardHeader>
              <CardTitle className='text-lg'>Recommendations</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className='space-y-2'>
                {data.recommendations.map((recommendation, index) => (
                  <li key={index} className='flex items-start gap-2'>
                    <div className='h-1.5 w-1.5 rounded-full bg-brand-60 mt-2 flex-shrink-0' />
                    <p className='text-sm text-gray-70'>{recommendation}</p>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </CardContent>
    </Card>
  );
};

export default ABCAnalysis;
